import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from "@/i18n/useTranslations";
import SubscriptionPreview from "@/pages/profile/channels/SubscriptionPreview";
import { CHANNELS_DISCOVER_PATH } from "@/pages/profile/channels/utils/channelDiscovery";
import HomeCarouselAnalyticsService from "@/services/homeCarouselAnalytics.service";
import ChannelSliderCard from "@/shared-components/cards/components/ChannelSliderCard";
import FHSlider from "@/shared-components/sliders/components/FHSlider";
import Heading from "@/shared-components/text/components/Heading";
import Text from "@/shared-components/text/components/Text";

import useCarouselImpressionTracking from "./hooks/useCarouselImpressionTracking";
import DiscoverHeaderControls from "./DiscoverHeaderControls";

import "./styles/HomeChannels.css";

const getChannelRoute = (channel) => {
  const channelId = channel?._id || channel?.id || channel?.channel_id || null;
  const ownerUsername =
    channel?.owner_username ||
    channel?.user_username ||
    channel?.channel_name ||
    null;

  if (channelId && ownerUsername) {
    return `/channel/${ownerUsername}/${channelId}`;
  }

  return CHANNELS_DISCOVER_PATH;
};

export default function HomeChannels({
  channels = [],
  title = "",
  trackingContext = {},
}) {
  const tHome = useTranslations("common.shared.home");
  const navigate = useNavigate();
  const { state } = useStateHandler();
  const sliderRef = useRef(null);
  const impressionRef = useCarouselImpressionTracking(trackingContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [previewChannel, setPreviewChannel] = useState(null);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;

    const handleScroll = () => setIsScrolled(slider.scrollLeft > 0);
    slider.addEventListener("scroll", handleScroll);

    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div ref={impressionRef} className="channel-slider__container">
      <div className="channel-slider__header">
        <Heading className="home-title">{title}</Heading>
        <DiscoverHeaderControls viewportRef={sliderRef} />
      </div>
      <div className="full-bleed">
        <FHSlider
          ref={sliderRef}
          speed={1.4}
          className={`channel-slider__slider ${isScrolled ? "scrolled" : ""}`}
          customPrevButton={<></>}
          customNextButton={<></>}
        >
          {channels.length > 0
            ? channels.map((channel, index) => (
              <div
                className="channel-slider__slider-item"
                key={channel?._id || channel?.id || `${channel?.owner_username || "channel"}-${index}`}
              >
                <ChannelSliderCard
                  channel={channel}
                  onVisit={() => {
                    const destination = getChannelRoute(channel);
                    HomeCarouselAnalyticsService.trackHomeCarouselEvent(
                      "carousel_item_click",
                      {
                        ...trackingContext,
                        itemId: channel?._id || channel?.id || channel?.channel_id || null,
                        itemKind: "channel",
                        destination,
                      },
                      { userSession: state.user_session }
                    );
                    if (destination === CHANNELS_DISCOVER_PATH) {
                      navigate(destination);
                      return;
                    }

                    setPreviewChannel(channel);
                  }}
                />
              </div>
            ))
            : (
              <div className="channel-slider__slider-item p-4 text-center text-gray-500">
                {tHome("empty.no_users")}
              </div>
            )}

          <div className="channel-slider__slider-item see-all-slide">
            <Text
              className="home-slider__see-all font-size-medium"
              onClick={() => {
                HomeCarouselAnalyticsService.trackHomeCarouselEvent(
                  "carousel_see_all_click",
                  {
                    ...trackingContext,
                    destination: CHANNELS_DISCOVER_PATH,
                  },
                  { userSession: state.user_session }
                );
                navigate(CHANNELS_DISCOVER_PATH);
              }}
            >
              {tHome("see_all")}
            </Text>
          </div>
        </FHSlider>
      </div>
      {previewChannel && (
        <SubscriptionPreview
          channelId={previewChannel._id || previewChannel.id || previewChannel.channel_id}
          ownerUsername={
            previewChannel.owner_username
            || previewChannel.user_username
            || previewChannel.channel_name
          }
          onClose={() => setPreviewChannel(null)}
        />
      )}
    </div>
  );
}
